import { useState } from "react";
import { Autocomplete } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";

import useAutocompleteAddress from "./useAutocompleteAddress";
import TextInput from "../common/TextInput";

import intl from "../../helper/intl";
import useStyles from "./AutocompleteAddress.styles";

export default function AutocompleteAddress({
  id,
  label,
  value,
  required,
  disabled,
  onChange,
  onInputChange,
  onFocus,
}) {
  const classes = useStyles();
  const { predictions, loadingState } = useAutocompleteAddress(value);

  // Once the user picks a suggestion, the address is already shown in the
  // field and in the confirmation line below the map, so the list is
  // redundant — hide it until the user actually types something new again.
  const [justSelected, setJustSelected] = useState(false);

  const selectOption = (option) => {
    setJustSelected(true);
    onChange(option);
    // The button just clicked/activated is about to be removed from the DOM;
    // move focus back to the input so keyboard/screen-reader users land
    // somewhere predictable instead of losing focus to <body>.
    document.getElementById(id)?.focus();
  };

  const options = predictions.map((prediction) => {
    const { formattedAddress } = prediction;
    return {
      label: formattedAddress,
      data: prediction,
    };
  });

  const hintId = id + '-hint';

  let statusMessage = '';
  if (loadingState) {
    statusMessage = intl.translate('general__loading');
  } else if (value) {
    if (options.length === 1) {
      statusMessage = intl.translate('register_page__address_result_found_singular');
    } else if (options.length > 1) {
      statusMessage = intl.translate('register_page__address_results_found', { count: options.length });
    } else {
      statusMessage = intl.translate('register_page__no_options');
    }
  }

  return (
    <div style={{ width: '100%' }}>
      <Autocomplete
        id={id}
        fullWidth
        freeSolo
        disablePortal
        disableClearable
        // The suggestions popper is intentionally never shown (see the real,
        // always-visible list below) — it's inaccessible to touch screen readers
        // (VoiceOver/TalkBack) since it relies on virtual focus. This component
        // still provides the combobox input, typing, and freeSolo behavior.
        open={false}
        onOpen={() => {}}
        onClose={() => {}}
        inputValue={value}
        options={options}
        filterOptions={(options) => options}
        onChange={(_, value) => onChange(value)}
        onFocus={onFocus}
        renderInput={(params) => {
          return (
            <TextInput
              listenToEvents
              required={required}
              disabled={disabled}
              label={label}
              onChange={(event) => {
                setJustSelected(false);
                onInputChange(event.target.value);
              }}
              isValid={(value) => !!value}
              errorMessage={intl.translate('general__empty_field_error')}
              {...params}
              inputProps={{
                ...params.inputProps,
                'aria-describedby': hintId,
              }}
            />
          );
        }}
      />
      <span id={hintId} style={visuallyHidden}>
        {intl.translate('register_page__address_usage_hint')}
      </span>
      <span style={visuallyHidden} aria-live="polite" aria-atomic="true">
        {statusMessage}
      </span>
      {options.length > 0 && !justSelected && (
        <div className={classes.suggestions}>
          <p className={classes['suggestions-heading']}>
            {intl.translate('register_page__address_suggestions_heading')}
          </p>
          <ul className={classes['suggestions-list']}>
            {options.map((option, index) => (
              <li key={index} className={classes['suggestions-item']}>
                <button
                  type="button"
                  className={classes['suggestions-button']}
                  onClick={() => selectOption(option)}
                >
                  <LocationOnIcon className={classes['suggestions-pin']} aria-hidden="true" />
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}